import { ChangeDetectionStrategy, Component, effect, inject, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { combineLatest, EMPTY, Observable, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { AddressSearchLogic } from '@mwe/services';
import { NgbHighlight, NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { AddressParts, FormatterType, FormFieldId, IOlavEntry, ISolrDocList, SearchableFieldId } from '@mwe/models';
import { dependencyMap, fieldFlow, formatterConfig, searchableFields } from './utils/utils';

const DEBOUNCE_TIME = 500;

@Component({
  selector: 'mwe-address-search',
  imports: [CommonModule, ReactiveFormsModule, NgbTypeahead, TranslatePipe, NgbHighlight],
  templateUrl: './address-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AddressSearchLogic],
})
export class AddressSearchComponent {
  private readonly fb = inject(FormBuilder);
  private readonly addressSearchLogic = inject(AddressSearchLogic);

  readonly inputContext = signal<FormFieldId | null>(null);
  readonly selectedOlav = model<IOlavEntry | null>(null);

  // Add the new output event emitter
  readonly addressInputCompleted = output<boolean>();

  private validatedFields = new Set<FormFieldId>();

  readonly formGroup = this.fb.group({
    zipCode: [undefined],
    location: [{ value: '', disabled: true }],
    street: [{ value: '', disabled: true }],
    building: [{ value: '', disabled: true }],
    door: [{ value: null, disabled: true }],
  });

  readonly fieldIDs: FormFieldId[][] = [
    ['zipCode', 'location'],
    ['street', 'building', 'door'],
  ];

  constructor() {
    this.search = this.search.bind(this);
    this.formatter = this.formatter.bind(this);

    effect(() => {
      const context = this.inputContext();
      if (context) {
        this.handleContextChange(context);
      }
    });
  }

  search(value$: Observable<string>) {
    const context = this.inputContext();
    if (context === 'location') return EMPTY;

    return combineLatest([value$, this.formGroup.valueChanges]).pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((previous, current) => {
        return previous[0] === current[0] && JSON.stringify(previous[1]) === JSON.stringify(current[1]);
      }),
      filter(([textInput, formValues]) => this.isValidSearchInput(textInput, formValues)),
      switchMap(([_, formValues]) => {
        const addressParts = this.buildAddressParts(formValues);
        return this.addressSearchLogic.search(addressParts) ?? EMPTY;
      }),
    );
  }

  onSelectItem({ item }: NgbTypeaheadSelectItemEvent<ISolrDocList>) {
    if (this.isNoResultsMessage(item)) {
      return;
    }

    const selectedEntry = item.docs?.[0];
    if (!selectedEntry) return;

    const context = this.inputContext();

    this.selectedOlav.set(selectedEntry);
    this.updateFormWithSelection(item);
    this.enableNextField(item);

    if (context) {
      this.addValidatorsAfterSelection(context);
      this.validatedFields.add(context);
    }

    // Only check completion after the full selection flow is complete
    setTimeout(() => this.checkAddressCompletion(), 0);
  }

  // Add method to check if address input is complete and valid
  private checkAddressCompletion(): void {
    const selectedOlav = this.selectedOlav();

    // Always emit false if no selected address
    if (!selectedOlav) {
      this.addressInputCompleted.emit(false);
      return;
    }

    const requiredFields: FormFieldId[] = ['zipCode', 'location', 'street', 'building'];
    const doorControl = this.formGroup.get('door');

    // Add door to required fields if it's enabled
    if (doorControl?.enabled) {
      requiredFields.push('door');
    }

    // Simple check: all required fields must be enabled, validated, and valid
    const allFieldsValid = requiredFields.every(fieldId => {
      const control = this.formGroup.get(fieldId);
      return control?.enabled && control?.valid && this.validatedFields.has(fieldId);
    });

    // Reuse existing door logic: if door is not enabled, check if it should be
    const doorHandledCorrectly = doorControl?.enabled || !this.shouldDoorBeEnabled(selectedOlav);

    const isComplete = allFieldsValid && doorHandledCorrectly;

    // Always emit the current state - true if complete, false if not
    this.addressInputCompleted.emit(isComplete);
  }

  // Extract and reuse the door checking logic from shouldEnableDoor
  private shouldDoorBeEnabled(selectedOlav: IOlavEntry): boolean {
    // Create a mock ISolrDocList to reuse the existing shouldEnableDoor logic
    const mockDocList: ISolrDocList = {
      docs: [selectedOlav],
      numFound: 1,
      start: 0,
    } as ISolrDocList;

    return this.shouldEnableDoor(mockDocList);
  }

  isControlEnabled(name: string) {
    return this.formGroup.get(name)?.enabled;
  }

  setContext(context: FormFieldId) {
    if (context !== 'location') {
      // Clear following fields when switching context to ensure clean state
      const fieldSequence: FormFieldId[] = ['zipCode', 'location', 'street', 'building', 'door'];
      const currentIndex = fieldSequence.indexOf(context);
      const nextFieldsToDisable = fieldSequence.slice(currentIndex + 1);

      nextFieldsToDisable.forEach(fieldName => {
        const control = this.formGroup.get(fieldName);
        if (control && control.enabled && fieldName !== 'location') {
          control.disable();
        }
      });

      this.inputContext.set(context);
    }
  }

  private handleContextChange(context: FormFieldId) {
    const dependentField = dependencyMap[context];
    if (dependentField) {
      this.clearFollowingInputs(dependentField);
    }
  }

  private enableNextField(selectedItem?: ISolrDocList) {
    const context = this.inputContext();
    const nextField = fieldFlow[context];

    if (!nextField) return;

    if (nextField === 'door') {
      const selectedOlav = this.selectedOlav();

      // Always re-evaluate door enablement based on the selected item
      if (selectedItem && this.shouldEnableDoor(selectedItem)) {
        this.enableAndFocusField(nextField);
      } else if (selectedOlav?.tuerNr) {
        const doorControl = this.formGroup.get('door');
        doorControl?.setValue(selectedOlav.tuerNr);
        doorControl?.enable();
        // Auto-validate door if it's auto-filled
        this.addValidatorsAfterSelection('door');
        this.validatedFields.add('door');
        // Check completion after auto-filling door with delay to ensure proper form state
        setTimeout(() => this.checkAddressCompletion(), 0);
      } else {
        // Make sure door is disabled if it shouldn't be enabled
        const doorControl = this.formGroup.get('door');
        if (doorControl?.enabled) {
          doorControl.disable();
          this.removeValidators('door');
        }
      }
      return;
    }

    this.enableAndFocusField(nextField);
  }

  private clearFollowingInputs(startingField: FormFieldId) {
    const fieldSequence: FormFieldId[] = ['zipCode', 'location', 'street', 'building', 'door'];
    const startIndex = fieldSequence.indexOf(startingField);

    if (startIndex === -1) return;

    fieldSequence.slice(startIndex).forEach(fieldName => {
      const control = this.formGroup.get(fieldName);
      if (!control) return;

      control.setValue('', { emitEvent: false });
      control.markAsPristine();
      control.markAsUntouched();

      // Remove validators from cleared fields
      this.removeValidators(fieldName);

      if (fieldName === 'location') {
        control.disable();
      } else if (fieldName !== 'zipCode') {
        control.disable();
      }
    });

    // Clear selectedOlav when clearing location or zipCode
    if (startingField === 'zipCode' || startingField === 'location') {
      this.selectedOlav.set(null);
    }

    // Only check completion after clearing is complete with delay
    setTimeout(() => this.checkAddressCompletion(), 0);
  }

  private isNoResultsMessage(item: ISolrDocList): boolean {
    // Check if item is a string (no results message) instead of a proper ISolrDocList
    return typeof item === 'string' || (Array.isArray(item) && typeof item[0] === 'string');
  }

  resultFormatter(input: ISolrDocList, context: FormFieldId = this.inputContext()) {
    // If it's a no results message, return it as-is
    if (this.isNoResultsMessage(input)) {
      return Array.isArray(input) ? input[0] : input;
    }

    return this.formatValue(input, 'result', context);
  }

  formatter(input: ISolrDocList, context: FormFieldId = this.inputContext()) {
    return this.formatValue(input, 'input', context);
  }

  readonly getLabelKey = (id: FormFieldId) => `address-input.inputLabel.${id}`;

  isSearchableField(fieldId: FormFieldId): boolean {
    return searchableFields.includes(fieldId as SearchableFieldId);
  }

  // Form validation helpers
  getIsInvalid(controlName: FormFieldId): boolean {
    const control = this.formGroup.get(controlName);
    // Only show invalid state if field has been validated through selection
    return !!(control?.errors && this.validatedFields.has(controlName) && (control.dirty || control.touched));
  }

  getIsValid(controlName: FormFieldId): boolean {
    const control = this.formGroup.get(controlName);
    // Only show valid state if field has been validated through selection
    return control?.status === 'VALID' && this.validatedFields.has(controlName) && (control.dirty || control.touched);
  }

  private createTypeaheadValidator(fieldId: FormFieldId): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.validatedFields.has(fieldId)) {
        return null;
      }

      const selectedOlav = this.selectedOlav();
      if (!selectedOlav) {
        return { typeaheadMismatch: true };
      }

      const controlValue = control.value;

      // Check if control value is the original ISolrDocList object from typeahead selection
      if (this.isValidTypeaheadObject(controlValue, selectedOlav, fieldId)) {
        return null;
      }

      // If control value is a string, check if it matches expected formatted value
      if (typeof controlValue === 'string') {
        const expectedValue = formatterConfig[fieldId]?.input?.(selectedOlav) ?? '';
        if (expectedValue === controlValue) {
          return null;
        }
      }

      return { typeaheadMismatch: true };
    };
  }

  private isValidTypeaheadObject(controlValue: any, selectedOlav: IOlavEntry, fieldId: FormFieldId): boolean {
    // Check if controlValue is an ISolrDocList object containing the selected entry
    if (controlValue?.docs && Array.isArray(controlValue.docs)) {
      const firstDoc = controlValue.docs[0];
      return firstDoc && this.isSameOlavEntry(firstDoc, selectedOlav);
    }

    return false;
  }

  private isSameOlavEntry(doc1: IOlavEntry, doc2: IOlavEntry): boolean {
    // Compare key properties to determine if they're the same entry
    return (
      doc1.olavKey === doc2.olavKey ||
      (doc1.plz === doc2.plz &&
        doc1.gemeinde === doc2.gemeinde &&
        doc1.strasse === doc2.strasse &&
        doc1.gebadr1 === doc2.gebadr1 &&
        doc1.tuerNr === doc2.tuerNr)
    );
  }

  private addValidatorsAfterSelection(fieldId: FormFieldId) {
    const control = this.formGroup.get(fieldId);
    if (!control) return;

    const baseValidators = this.getBaseValidators(fieldId);
    const typeaheadValidator = this.createTypeaheadValidator(fieldId);

    control.setValidators([...baseValidators, typeaheadValidator]);
    control.updateValueAndValidity();
  }

  private getBaseValidators(fieldId: FormFieldId): ValidatorFn[] {
    switch (fieldId) {
      case 'zipCode':
        return [Validators.required, Validators.maxLength(4)];
      case 'location':
      case 'street':
      case 'building':
        return [Validators.required];
      case 'door':
        return this.formGroup.get('door')?.enabled ? [Validators.required] : [];
      default:
        return [];
    }
  }

  private removeValidators(fieldId: FormFieldId) {
    const control = this.formGroup.get(fieldId);
    if (!control) return;

    control.clearValidators();
    control.updateValueAndValidity();
    this.validatedFields.delete(fieldId);

    // Check completion after removing validators to emit false if needed
    setTimeout(() => this.checkAddressCompletion(), 0);
  }

  private formatValue(input: ISolrDocList, type: FormatterType, context: FormFieldId): string {
    if (!context) return '';
    if (this.isNoResultsMessage(input)) return '';

    const doc = input?.docs?.[0];
    if (!doc) return input as unknown as string;

    return formatterConfig[context]?.[type]?.(doc) ?? '';
  }

  private shouldEnableDoor(selectedDocList: ISolrDocList): boolean {
    return (
      selectedDocList.docs.some(
        doc =>
          (doc.tuerNr && doc.tuerNr.trim() !== '') || doc.gebadr1 !== doc.anladr1 || doc.identAdresse || doc.strasse !== doc.identStrasse,
      ) ||
      (selectedDocList.numFound > 1 && selectedDocList.docs.some(doc => doc.hasOwnProperty('tuerNr')))
    );
  }

  private isValidSearchInput(textInput: string, formValues: any): boolean {
    if (!textInput?.trim()) return false;

    const context = this.inputContext();

    // Special validation for zipCode context
    if (context === 'zipCode') {
      // Only allow search if there are more than 3 digits
      const digitCount = (textInput.match(/\d/g) || []).length;
      return digitCount >= 3;
    }

    this.checkAddressCompletion();

    const values = Object.values(formValues).filter(Boolean);
    return values.some(v => v?.toString().toLowerCase() === textInput.toLowerCase());
  }

  private buildAddressParts(formValues: any): AddressParts {
    return {
      zipCode: this.formatAddressPart(formValues?.zipCode, 'zipCode'),
      location: this.formatAddressPart(formValues?.location, 'location'),
      street: this.formatAddressPart(formValues?.street),
      building: this.formatAddressPart(formValues?.building),
      door: this.formatAddressPart(formValues?.door),
      useIdentSearch: this.determineUseIdentSearch()?.toString(),
    };
  }

  private determineUseIdentSearch(): boolean {
    const selectedOlav = this.selectedOlav();
    const context = this.inputContext();

    // Only apply useIdentSearch logic when searching for door field
    if (context !== 'door' || !selectedOlav) {
      return false;
    }

    // Same logic as in address-input.component.ts
    return !!selectedOlav.identAdresse || selectedOlav.strasse !== selectedOlav.identStrasse;
  }

  private formatAddressPart(value: string | undefined, type?: 'zipCode' | 'location'): string {
    const currentContext = this.inputContext();
    const selectedOlav = this.selectedOlav();

    if (!value) {
      if (type === 'zipCode') {
        const currentValue = this.formGroup.value?.zipCode as string;
        return currentValue?.length ? currentValue.slice(0, 4) : '*';
      }
      if (type === 'location') {
        const currentValue = this.formGroup.value?.location as string;
        return currentValue?.length ? currentValue : '*';
      }
      return '';
    }

    // For zipCode field
    if (type === 'zipCode') {
      // If this is the current input field, add wildcard
      if (currentContext === 'zipCode') {
        return `${value}*`.slice(0, 4);
      }
      // Otherwise, use exact selected value
      return selectedOlav?.plz || value;
    }

    // For other searchable fields
    const fieldId = this.getFieldIdFromValue(value, type);
    if (fieldId && currentContext === fieldId) {
      // If this is the current input field, add wildcard
      return `${value}*`;
    }

    // Use exact selected value based on field type
    if (selectedOlav) {
      switch (fieldId || type) {
        case 'street':
          return selectedOlav.strasse || value;
        case 'building':
          return selectedOlav.gebadr1 || value;
        case 'door':
          return selectedOlav.tuerNr || value;
        case 'location':
          return selectedOlav.gemeinde || value;
        default:
          return value;
      }
    }

    return value;
  }

  private getFieldIdFromValue(value: string, type?: string): FormFieldId | null {
    // Helper method to determine which field this value belongs to
    // This might need adjustment based on how you can identify the field
    const formValues = this.formGroup.value;

    if (formValues.street === value) return 'street';
    if (formValues.building === value) return 'building';
    if (formValues.door === value) return 'door';
    if (formValues.location === value) return 'location';

    return null;
  }

  private updateFormWithSelection(item: ISolrDocList) {
    const context = this.inputContext();
    const updates: Record<string, any> = {
      [context]: this.formatter(item),
    };

    if (context === 'zipCode') {
      const locationValue = item?.docs?.[0]?.gemeinde || '';
      updates.location = locationValue;

      const locationControl = this.formGroup.get('location');
      if (locationControl && locationValue) {
        locationControl.enable();
        // Add validators for location field as well
        this.addValidatorsAfterSelection('location');
        this.validatedFields.add('location');
      }
    }

    this.formGroup.patchValue(updates, { emitEvent: false });
  }

  private enableAndFocusField(fieldId: FormFieldId) {
    const control = this.formGroup.get(fieldId);
    control?.enable();
    this.focusField(fieldId);
  }

  private focusField(fieldId: FormFieldId) {
    setTimeout(() => {
      const element = document.getElementById(fieldId) as HTMLInputElement;
      if (element && !element.disabled) {
        element.focus();
      }
    }, 0);
  }
}
